import axios from 'axios';
import { storeAction } from "../storeActions.constant";
import { storeGetters } from "../storeGetters.constant";
import { storeMutation } from "../storeMutations.constant";
import { getBasicHeaders } from "../../common/utils/utils";
import {
    createRibbonState,
    memberDirectoryPageConstant,
    createTypeAheadFilters,
    filterRibbonListByOrder,
    createSubFilters,
    defaultSort,
} from "../../components/MemberDirectoryPage/memberDirectoryPage.constants";
import { memberDirectorySearchObject, memberDirectoryTypeAheadObject } from "../store.constant";
import { wrapAction } from "../../common/utils/actionLifecycle";
import { getAxiosCancelTokenSource } from "../../app.utils";
import uniqBy from 'lodash/uniqBy';

const memberDirectoryState = () => {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        memberDirectorySearchResults: null,
        memberDirectorySort: null,
        nextMemberDirectorySearchOffset: 0,
        sortByLastNameLetter: null,
        ribbonState: null,
        selectedFilters: null,
        searchText: '',
        filtersFromRoute: null,
        selectedTypeAhead: null,
        subFilterList: null,
        selectedSubFilter: null,
        typeAheadForMemberDirectory: null,
        memberConnections: {
            connectedTo: null,
            connectionOf: null,
        },
        memberFollow: {
            followerOf: null,
            followedBy: null,
        },
        disableMemberDirectoryInfiniteScroll: false,
        selectedLetterFilter: null,
    };
};

const replaceSelectedFilterItems = (state, searchRibbonGroups) => {
    const newFilterItems = [];
    (searchRibbonGroups || []).forEach((g) => {
        g.filters.forEach((f) => {
            if (f.subFilters && f.subFilters.length) {
                newFilterItems.push(...f.subFilters);
                f.subFilters.forEach((sf) => {
                    if (sf.subFilters && sf.subFilters.length) {
                       newFilterItems.push(...sf.subFilters);
                    }
                });
            }
            newFilterItems.push(f);
        });
    });

    const replace = ((originalFilters, newItems) => {
        const filters = {};
        Object.keys(originalFilters).forEach((key) => {
            const list = originalFilters[key] || [];
            filters[key] = list.reduce((l, item) => {
                if (item.isTypeAhead) {
                    l.push(item);
                    return l;
                }

                const newItem = newItems
                    .find(it =>
                        it.filterKey === item.filterKey &&
                        it.filterValue === item.filterValue);
                if (newItem) l.push(newItem);
                else l.push(item);
                return l;
            }, []);
        });
        return filters;
    });

    if (state.ribbonState) {
        state.ribbonState = replace(state.ribbonState, newFilterItems);
    }
    if (state.selectedFilters) {
        state.selectedFilters = replace(state.selectedFilters, newFilterItems);
    }
};

const memberDirectoryPageMutations = {
    [storeMutation.setMemberDirectorySearchResults]: (state, data) => {        
        replaceSelectedFilterItems(state, data.searchRibbonGroup);
        state.memberDirectorySearchResults = {
            ...data,
            searchResults: data.searchResults || [],
            searchRibbonGroup: data.searchRibbonGroup ? filterRibbonListByOrder(data.searchRibbonGroup) : [],
        };
        state.nextMemberDirectorySearchOffset = memberDirectoryPageConstant.constant.searchResultOffsetStep;
    },
    [storeMutation.setMemberDirectoryWithLastNameSearchResult]: (state, data) => {
        replaceSelectedFilterItems(state, data.searchRibbonGroup);
        state.memberDirectorySearchResults = {
            ...data,
            searchResults: data.searchResults || [],
            searchRibbonGroup: data.searchRibbonGroup ? filterRibbonListByOrder(data.searchRibbonGroup) : [],
        };
        state.nextMemberDirectorySearchOffset += memberDirectoryPageConstant.constant.searchResultOffsetStep;
    },
    [storeMutation.removeSelectedPrivateNetworks]: (state, data) => {
        if (data && data.length && state.ribbonState
            && state.selectedFilters
            && state.selectedFilters[memberDirectoryPageConstant.constant.networkAndGroupsKey]) {
            const filteredResult =
                state.selectedFilters[memberDirectoryPageConstant.constant.networkAndGroupsKey]
                    .filter(stItem =>
                        !data.includes(data
                            .find(it => it.filterKey === stItem.filterKey && it.filterValue === stItem.filterValue)));
            state.selectedFilters[memberDirectoryPageConstant.constant.networkAndGroupsKey] = [...filteredResult];
            state.ribbonState[memberDirectoryPageConstant.constant.networkAndGroupsKey] = [...filteredResult];
        }
    },
    [storeMutation.appendToMemberDirectorySearchResults]: (state, data) => {
        if (data.searchResults && data.searchResults.length) {
            replaceSelectedFilterItems(state, data.searchRibbonGroup);
            state.memberDirectorySearchResults = {
                ...data,
                searchResults: [...(state.memberDirectorySearchResults.searchResults || []), ...(data.searchResults || [])],
                searchRibbonGroup: data.searchRibbonGroup ? filterRibbonListByOrder(data.searchRibbonGroup) : [],
            };

            state.memberDirectorySearchResults.searchResults = uniqBy(state.memberDirectorySearchResults.searchResults, 'id');
            state.nextMemberDirectorySearchOffset += memberDirectoryPageConstant.constant.searchResultOffsetStep;
        }
    },
    [storeMutation.setMemberDirectorySort]: (state, value) => {
        state.memberDirectorySort = value;
        if (window.sessionStorage) {
            sessionStorage.memberDirectorySort = JSON.stringify(value);
        }
    },
    [storeMutation.setRibbonState]: (state) => {
        state.ribbonState = createRibbonState(state.memberDirectorySearchResults.searchRibbonGroup);
        state.selectedFilters = createRibbonState(state.memberDirectorySearchResults.searchRibbonGroup);
    },

    [storeMutation.setRoutedRibbonState]: (state) => {
        state.ribbonState = { ...state.ribbonState, ...state.filtersFromRoute };
        state.selectedFilters = { ...state.selectedFilters, ...state.filtersFromRoute };
        state.filtersFromRoute = { ...state.ribbonState };
        replaceSelectedFilterItems(state, state.memberDirectorySearchResults.searchRibbonGroup);
    },
    [storeMutation.removeConnectedFromConnection]: (state, user) => {
       if (user && state.memberDirectorySearchResults && state.memberDirectorySearchResults.searchResults) {
           const member = state.memberDirectorySearchResults.searchResults.find(person => person.id === user.id);
           const memberIndex = state.memberDirectorySearchResults.searchResults.indexOf(member);
          if (memberIndex >= 0) {
              state.memberDirectorySearchResults.searchResults.splice(memberIndex, 1);
          }
       }
    },
    [storeMutation.locallySetFollowConnect]: (state, user) => {
        if (user.hasOwnProperty('isConnected')) {
            // eslint-disable-next-line no-nested-ternary
            state.memberConnections.connectionOf = user.isConnected
                ? state.memberConnections.connectionOf += 1
                : state.memberConnections.connectionOf
                    ? state.memberConnections.connectionOf -= 1
                    : 0;
        }
        if (state.memberDirectorySearchResults) {
            state.memberDirectorySearchResults.searchResults.some((member) => {
                if (user.id === member.id) {
                    if (user.hasOwnProperty('isConnected')) member.isConnected = user.isConnected;
                    return true;
                }
                return false;
            });
        }
    },
    [storeMutation.setMemberConnectionListInState]: (state, data) => {
        if (data) {
            state.memberConnections.connectedTo =
                data.connectedTo && data.connectedTo.length ? data.connectedTo.length : null;
            state.memberConnections.connectionOf =
                data.connectionOf && data.connectionOf.length ? data.connectionOf.length : null;
        }
    },
    [storeMutation.setMemberFollowListInState]: (state, data) => {
        if (data) {
            state.memberFollow.followerOf =
                data.followerOf && data.followerOf.length ? data.followerOf.length : null;
            state.memberFollow.followedBy =
                data.followedBy && data.followedBy.length ? data.followedBy.length : null;
        }
    },
    [storeMutation.resetRibbonState]: (state) => {
        state.ribbonState = { ...state.selectedFilters };
        state.selectedTypeAhead = null;
        state.selectedSubFilter = null;
        if (state.typeAheadForMemberDirectory) {
            state.typeAheadForMemberDirectory.forEach((item) => {
                item.isActive = false;
                item.searchText = '';
                item.searchResult = null;
            });
        }
        if (state.subFilterList) {
            state.subFilterList.forEach((item) => {
                item.isActive = false;
            });
        }
    },
    [storeMutation.setRibbonStateFromRouter]: (state, dataFromRoute) => {
        const { searchText, filters } = dataFromRoute;
        state.filtersFromRoute = { ...filters };
        state.ribbonState = { ...filters };
        state.selectedFilters = { ...filters };
        state.searchText = searchText;
    },
    [storeMutation.setSelectedFilters]: (state) => {
        state.selectedFilters = { ...state.ribbonState };
    },
    [storeMutation.clearRibbonState]: (state) => {
        if (state.memberDirectorySearchResults && state.memberDirectorySearchResults.searchRibbonGroup) {
            state.selectedFilters = createRibbonState(state.memberDirectorySearchResults.searchRibbonGroup);
            state.ribbonState = createRibbonState(state.memberDirectorySearchResults.searchRibbonGroup);
        } else {
            state.selectedFilters = null;
            state.ribbonState = null;
        }
        state.filtersFromRoute = null;
    },
    [storeMutation.removePillFromState]: (state, pill) => {
        if (state.selectedFilters) {
            Object.keys(state.selectedFilters).forEach((ribbonItem) => {
                if (state.selectedFilters.hasOwnProperty(ribbonItem)) {
                    const itemFound = state.selectedFilters[ribbonItem].find(item => item.label === pill.label);
                    if (itemFound) {
                        state.selectedFilters[ribbonItem].splice(state.selectedFilters[ribbonItem].indexOf(itemFound), 1);
                    }
                }
            });
            state.ribbonState = { ...state.selectedFilters };
        }
    },
    [storeMutation.setOneClickChapterInState]: (state, selectedChapterArray) => {
        if (state.selectedFilters) {
            if (selectedChapterArray && selectedChapterArray.length) {
                state.selectedFilters = {
                    ...state.selectedFilters,
                    [memberDirectoryPageConstant.constant.selectedOneClickChapter]: [...selectedChapterArray],
                };
            } else {
                state.selectedFilters = {
                    ...state.selectedFilters,
                    chapterAndRegions: [],
                    [memberDirectoryPageConstant.constant.selectedOneClickChapter]: [],
                };
            }
            state.ribbonState = { ...state.selectedFilters };
        }
    },

    [storeMutation.setSkipCount]: (state, count) => {
        state.nextMemberDirectorySearchOffset = count;
    },
    [storeMutation.setSelectedTypeAhead]: (state, data) => {
        if (data) {
            const { parentKey, activeTypeAhead } = data;
            let preData = null;
            if (parentKey && state.selectedFilters && state.selectedFilters[parentKey] && state.selectedFilters[parentKey].length > 0) {
                if (parentKey === memberDirectoryPageConstant.constant.memberLocationKey) {
                    preData = state.selectedFilters[parentKey];
                } else {
                    preData = state.selectedFilters[parentKey].filter(item => item.filterKey === activeTypeAhead.key);
                }
            }
            state.selectedTypeAhead = {
                ...activeTypeAhead,
                previouslySelected: preData,
            };
        } else {
            state.selectedTypeAhead = null;
        }
        state.selectedSubFilter = null;
        if (state.subFilterList) {
            state.subFilterList.forEach((item) => {
                item.isActive = false;
            });
        }
    },
    [storeMutation.initializeMemberTypeAhead]: (state, data) => {
        state.typeAheadForMemberDirectory = createTypeAheadFilters(data);
    },
    [storeMutation.initializeSubFilters]: (state, data) => {
        state.subFilterList = createSubFilters(data);
    },
    [storeMutation.setMemberDirectoryTypeAhead]: (state, key) => {
        state.typeAheadForMemberDirectory.forEach((item) => {
            item.searchText = '';
            if (item.key.toLowerCase() === key.toLowerCase()) {
                item.isActive = !item.isActive;
            } else {
                item.isActive = false;
            }
        });
    },
    [storeMutation.setSubFilterList]: (state, filter) => {
        if (state.subFilterList) {
            state.subFilterList.forEach((item) => {
                if (item.key.toLowerCase() === filter.filterKey.toLowerCase() && item.value.toLowerCase() === filter.filterValue.toLowerCase()) {
                    item.isActive = !item.isActive;
                } else {
                    item.isActive = false;
                }
            });
        }
    },
    [storeMutation.setSelectedSubFilter]: (state, data) => {
        state.selectedSubFilter = data;
        state.selectedTypeAhead = null;
        if (state.typeAheadForMemberDirectory) {
            state.typeAheadForMemberDirectory.forEach((item) => {
                item.isActive = false;
                item.searchText = '';
                item.searchResult = null;
                item.previouslySelected = null;
            });
        }
    },
    [storeMutation.setTypeAheadResult]: (state, data) => {
        state.selectedTypeAhead.searchResult = data.result;
    },
    [storeMutation.setMemberDirectorySearchText]: (state, data) => {
        state.searchText = data;
    },
    [storeMutation.resetTypeAheadResult]: (state) => {
        state.selectedTypeAhead.searchResult = null;
    },
    [storeMutation.removeNestedCheckBoxFilters]: (state, filterData) => {
        const { parentKey, filter } = filterData;
        if (parentKey && filter) {
            const itemValue = filter.filterValue.split("");
            itemValue.pop();
            if (itemValue) {
                const itFormatted = `${itemValue.join("")}>`;
                state.ribbonState[parentKey] = state.ribbonState[parentKey]
                    .filter(stItem => stItem.filterKey === filter.filterKey && !stItem.filterValue.includes(itFormatted));
            }
        }
    },
    [storeMutation.setMemberDirectoryInfiniteScrollAbility]: (state, val) => {
        state.disableMemberDirectoryInfiniteScroll = val;
    },
    [storeMutation.setMemberSelectedLetterFilter]: (state, filter) => {
        state.selectedLetterFilter = filter || null;
        if (!filter && state.selectedFilters && state.selectedFilters.hasOwnProperty('letterFilter')) {
            delete state.selectedFilters.letterFilter;
        }
    },
    [storeMutation.updateMemberSelectedLetterFilter]: (state, filterFromStorage) => {
        state.selectedLetterFilter = null;
        if (filterFromStorage) {
            const parsedFiltersFromStorage = JSON.parse(filterFromStorage);

            if (parsedFiltersFromStorage && parsedFiltersFromStorage.hasOwnProperty('letterFilter')) {
                state.selectedLetterFilter = { ...parsedFiltersFromStorage.letterFilter[0] };
            }
        }
    },
};

const memberDirectoryPageActions = {
    [storeAction.getInitialMemberDirectorySearchResults]: wrapAction(
        storeAction.getInitialMemberDirectorySearchResults,
        ({ state, commit, dispatch }, additionalFilter) => {
            commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, true);

            return dispatch(storeAction.getMemberDirectorySearchResults, { skip: 0, additionalFilter })
                .then((response) => {
                    commit(storeMutation.setMemberDirectorySearchResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.memberDirectorySearchResults.searchResults
                        && response.data.totalResultCount > state.memberDirectorySearchResults.searchResults.length) {
                        commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    }

                    commit(storeMutation.initializeMemberTypeAhead, response.data.searchRibbonGroup);
                    commit(storeMutation.initializeSubFilters, response.data.searchRibbonGroup);
                    commit(storeMutation.setRibbonState);
                    commit(storeMutation.setRoutedRibbonState);
                    commit(storeMutation.removeSelectedPrivateNetworks, response.data.excludedPrivateNetworkFilters);

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    console.throw(err);
                });
        },
    ),
    [storeAction.getMemberDirectorySearchResultByLastName]: wrapAction(
        storeAction.getMemberDirectorySearchResultByLastName,
        ({ state, commit, dispatch }, additionalFilter) => {
            const skip = state.nextMemberDirectorySearchOffset;
            commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, true);

            return dispatch(storeAction.getMemberDirectorySearchResults, { skip, additionalFilter })
                .then((response) => {
                    commit(storeMutation.setMemberDirectoryWithLastNameSearchResult, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.memberDirectorySearchResults.searchResults
                        && response.data.totalResultCount > state.memberDirectorySearchResults.searchResults.length) {
                        commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    }

                    commit(storeMutation.initializeMemberTypeAhead, response.data.searchRibbonGroup);
                    commit(storeMutation.initializeSubFilters, response.data.searchRibbonGroup);
                    commit(storeMutation.setRibbonState);
                    commit(storeMutation.setRoutedRibbonState);
                    commit(storeMutation.removeSelectedPrivateNetworks, response.data.excludedPrivateNetworkFilters);

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    console.throw(err);
                });
        },
    ),
    [storeAction.getNextMemberDirectorySearchResults]: wrapAction(
        storeAction.getNextMemberDirectorySearchResults,
        ({ state, commit, dispatch }, additionalFilter) => {
            const skip = state.nextMemberDirectorySearchOffset;
            commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, true);

            return dispatch(storeAction.getMemberDirectorySearchResults, { skip, additionalFilter })
                .then((response) => {
                    commit(storeMutation.appendToMemberDirectorySearchResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.memberDirectorySearchResults.searchResults
                        && response.data.totalResultCount > state.memberDirectorySearchResults.searchResults.length) {
                        commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    }

                    commit(storeMutation.initializeMemberTypeAhead, response.data.searchRibbonGroup);
                    commit(storeMutation.initializeSubFilters, response.data.searchRibbonGroup);
                    if (!state.filtersFromRoute) {
                        commit(storeMutation.setRibbonState);
                    }
                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setMemberDirectoryInfiniteScrollAbility, false);
                    console.throw(err);
                });
        },
    ),
    [storeAction.getMemberDirectorySearchResults]: ({ state, getters }, { skip, additionalFilter }) => {
        const sortObject = getters[storeGetters.memberDirectorySort];
        const sortString = `${sortObject.sortField} ${sortObject.direction}`;
        const searchKey = '';
        const requestBody = memberDirectorySearchObject(state.searchText, sortString, searchKey, state.selectedFilters, skip);
        if (additionalFilter) requestBody.searchFilters.push(...additionalFilter);

        return axios({
            //mock for local testing
            //url: '/mocks/memberSearchResult.json',
            url: `${state.baseURL}profiles/api/memberdirectory`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
            //method: 'GET',
        });
    },
    [storeAction.initiatePeopleSearch]: wrapAction(
        storeAction.initiatePeopleSearch,
        ({ state, commit }) => {
            //handling race condition
            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.initiatePeopleSearch,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            const requestBody = { search: state.searchText };
            return axios({
                //mock for local testing
                //url: '/mocks/memberFilterRibbonList.json',
                url: `${state.baseURL}profiles/api/personsearchautocomplete`,
                headers: getBasicHeaders(),
                cancelToken: requestSource.token,
                data: requestBody,
                method: 'POST',
            })
                .then((response) => {
                    commit(storeMutation.removeCancelableRequest, storeAction.initiatePeopleSearch, { root: true });
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.initiateTypeAheadSearch]: wrapAction(
        storeAction.initiateTypeAheadSearch,
        ({ commit, state }, typeAheadObject) => {
            //handling race condition
            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.initiateTypeAheadSearch,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            const { searchText, additionalFilters, typeAheadAdditionalFilter } = typeAheadObject;
            const requestBody = memberDirectoryTypeAheadObject(searchText, state.searchText, state.selectedTypeAhead, state.selectedFilters, additionalFilters, typeAheadAdditionalFilter);

            return axios({
                //mock for local testing
                //url: '/mocks/memberDirectoryTypeAhead.json',
                url: `${state.baseURL}profiles/api/typeAhead`,
                data: requestBody,
                cancelToken: requestSource.token,
                headers: getBasicHeaders(),
                method: 'POST',
            })
                .then((response) => {
                    commit(storeMutation.removeCancelableRequest, storeAction.initiateTypeAheadSearch, { root: true });
                    commit(storeMutation.setTypeAheadResult, response.data);
                    return response;
                })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getSalesforceRedirectUrl]: ({ state }, id) => {
        return axios({
            url: `${state.baseURL}profiles/api/managecontact/${id}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.setPersonConnect]: ({ commit, state }, obj) => {
        return axios({
            url: `${state.baseURL}profiles/api/member-profile/save-memberprofile-connection`,
            data: {
                'connectedPersonId': obj.user.id,
                'isConnected': obj.isConnected,
            },
            headers: getBasicHeaders(),
            method: 'POST',
        })
            .then((response) => {
                commit(storeMutation.locallySetFollowConnect, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.getMemberConnectionList]: ({ commit, state }, userId) => {
        return axios({
            url: `${state.baseURL}profiles/api/member-profile/get-memberprofile-connection/lists/${userId}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setMemberConnectionListInState, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.getMemberFollowList]: ({ commit, state }, userId) => {
        return axios({
            url: `${state.baseURL}profiles/api/member-profile/get-memberprofile-follow-status/lists/${userId}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setMemberFollowListInState, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.clearRibbonState]: ({ commit, dispatch }, additionalFilter) => {
        commit(storeMutation.clearRibbonState);
        dispatch(storeAction.getInitialMemberDirectorySearchResults, additionalFilter);
    },
};

const memberDirectoryPageGetters = {
    [storeGetters.memberDirectorySearchResults]: state => state.memberDirectorySearchResults,
    [storeGetters.memberResultList]: state => state.memberDirectorySearchResults && state.memberDirectorySearchResults.searchResults,
    [storeGetters.ribbonState]: state => state.ribbonState,
    [storeGetters.selectedMemberDirectoryFilters]: state => state.selectedFilters,
    [storeGetters.selectedTypeAhead]: state => state.selectedTypeAhead,
    [storeGetters.typeAheadForMemberDirectory]: state => state.typeAheadForMemberDirectory,
    [storeGetters.selectedFilterKeyForSecondaryOptions]: state => state.selectedFilterKeyForSecondaryOptions,
    [storeGetters.subFilterList]: state => state.subFilterList,
    [storeGetters.selectedSubFilter]: state => state.selectedSubFilter,
    [storeGetters.excludedPrivateNetworksList]: state => state.memberDirectorySearchResults
        && state.memberDirectorySearchResults.excludedPrivateNetworkFilters,
    [storeGetters.memberDirectorySort]: (state) => {
        if (state.memberDirectorySort) return state.memberDirectorySort;

        if (window.sessionStorage && sessionStorage.memberDirectorySort) {
            try {
                const sort = JSON.parse(sessionStorage.memberDirectorySort);
                if (sort) return sort;
            } catch (e) { /* pass */ }
        }
        return defaultSort;    
    },
    [storeGetters.memberConnection]: state => state.memberConnections,
    [storeGetters.memberFollow]: state => state.memberFollow,
    [storeGetters.memberDirectorySearchText]: state => state.searchText,
    [storeGetters.disableMemberDirectoryInfiniteScroll]: state => state.disableMemberDirectoryInfiniteScroll,
    [storeGetters.nextMemberDirectorySearchOffset]: state => state.nextMemberDirectorySearchOffset,
    [storeGetters.memberDirectorySelectedLetterFilter]: state => state.selectedLetterFilter,
};

export default {
    namespaced: true,
    state: {
        initialState: memberDirectoryState(),
        ...memberDirectoryState(),
    },
    mutations: { ...memberDirectoryPageMutations },
    actions: { ...memberDirectoryPageActions },
    getters: { ...memberDirectoryPageGetters },
};
